import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { capacitorService } from '../../../../application/CapacitorService';
import { LogoutIcon } from '../../../../assets/svg/LogoutIcon';
import { SlidingMenu } from '../../../../components/SlidingMenu';
import { EventTrackTypes } from '../../../../constants/EventTracks';
import * as routing from '../../../../constants/Routing';
import { useSessionLogout } from '../../../../hooks/session/useSessionLogout';
import { useCaptureEvents } from '../../../../hooks/useCaptureEvents';
import Button from '../../../../react-components/Button';
import { Text } from '../../../../react-components/Text';
import { AccountDeletionComponent } from '../../components/accountDeletion/AccountDeletionComponent';
import { usePermissions } from '../../hooks/usePermissions';
import { useRetrieveVehicle } from '../../hooks/useRetrieveVehicle';
import { AppInformation } from './AppInformation';
import { HomeSlidingMenuHeader } from './HomeSlidingMenuHeader';
import { RoutePreferences } from './RoutePreferences';
import { SubscriptionPill } from './SubscriptionPill';
import { VehiclePreferences } from './VehiclePreferences';

interface HomeSlidingMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HomeSlidingMenu = ({ isOpen, onClose }: HomeSlidingMenuProps) => {
  const navigate = useNavigate();
  const { isSoloDriver, canSubscribe } = usePermissions();
  const { data: vehicleDetails } = useRetrieveVehicle();
  const { run: logout, success: logoutSuccess } = useSessionLogout();
  const { capture } = useCaptureEvents();
  const [showAccountDeletionDialog, setShowAccountDeletionDialog] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const appVersion = capacitorService.getApplicationVersion();

  useEffect(() => {
    if (logoutSuccess) {
      navigate(routing.AUTH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoutSuccess]);

  if (!vehicleDetails) return null;

  return (
    <>
      <SlidingMenu
        id="vehicle-preferences"
        isOpen={isOpen}
        direction="down"
        timeout={{ enter: 400, exit: 300 }}
        headerStyles={{ backgroundColor: RoutalPalette.white, padding: `20px` }}
        childrenStyles={{ backgroundColor: RoutalPalette.white, padding: 0 }}
        onClose={onClose}
        headerContent={<HomeSlidingMenuHeader />}
        footerContent={
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            wrap="nowrap"
            style={{
              padding: `0 20px`,
              backgroundColor: RoutalPalette.white,
            }}
          >
            <Button
              variant="flat"
              style={{
                height: `40px`,
                fontWeight: `normal`,
                border: `none`,
                display: `flex`,
                justifyContent: `flex-start`,
                alignItems: `center`,
                fontSize: `16px`,
                padding: 0,
              }}
              onClick={(e: any) => {
                e.stopPropagation();
                logout();
              }}
            >
              <LogoutIcon color={RoutalPalette.text} />

              <HSpacer small />

              <Text
                variant="button"
                intlId="vehicle_drivers.preferences.logout"
                style={{ color: RoutalPalette.text }}
              />
            </Button>
            {appVersion && (
              <div onClick={() => setShowVersionModal(true)}>
                <Text variant={`ultra-small`}>{appVersion}</Text>
              </div>
            )}
          </Grid>
        }
      >
        <Grid
          style={{
            height: `100%`,
            width: `100%`,
            overflowY: `hidden`,
            background: RoutalPalette.white,
          }}
        >
          <Grid
            container
            direction="column"
            style={{
              background: RoutalPalette.white,
              overflowY: `auto`,
              display: `flex`,
              flexDirection: `column`,
              flexWrap: `nowrap`,
              padding: `20px 20px 0px 20px`,
              height: `100%`,
              flex: 1,
            }}
          >
            <Grid>
              {canSubscribe && (
                <>
                  <SubscriptionPill />
                  <VSpacer large />
                </>
              )}
              <VehiclePreferences />
              <VSpacer large />
              <RoutePreferences />
              <VSpacer large />

              {isSoloDriver ? (
                <>
                  <Text variant="label" intlId="vehicle_drivers.preferences.danger_zone" style={{ fontSize: `16px` }} />

                  <VSpacer medium />

                  <Grid
                    container
                    direction="column"
                    gap="12px"
                    style={{
                      padding: `12px`,
                      borderRadius: `10px`,
                      border: `1px solid ${RoutalPalette.error.light}`,
                    }}
                  >
                    <Text
                      variant="label"
                      intlId="vehicle_drivers.preferences.delete_account.info"
                      style={{ fontSize: `13px`, textAlign: `center` }}
                    />

                    <Button
                      style={{
                        backgroundColor: RoutalPalette.error.medium,
                        border: `unset`,
                        color: RoutalPalette.white,
                      }}
                      intlId="vehicle_drivers.preferences.delete_account"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        capture({
                          type: EventTrackTypes.DeleteAccount,
                          payload: {
                            [`Action`]: `Open`,
                          },
                        });
                        setShowAccountDeletionDialog(true);
                      }}
                    />
                  </Grid>

                  <VSpacer large />
                </>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      </SlidingMenu>
      {capacitorService.isNative() && (
        <AppInformation open={showVersionModal} onClose={() => setShowVersionModal(false)} />
      )}
      <AccountDeletionComponent open={showAccountDeletionDialog} onClose={() => setShowAccountDeletionDialog(false)} />
    </>
  );
};
